import { Type } from '@sinclair/typebox';
// M27 — DB-backed login-OAuth + Drive-connector-OAuth contracts (ADM-07/18;
// HUB-083 §M27). Client-secret material (plaintext OR ciphertext) is
// deliberately ABSENT from every response schema — it never leaves the DB row;
// only a `hasClientSecret` presence boolean is exposed. The connector OAuth app
// is a SEPARATE store from login SSO (§4 D-18).
/** Login-SSO provider config (response) — carries only `hasClientSecret`; `params` per HUB-117. */
export const OauthProviderConfigSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    provider: Type.String(),
    clientId: Type.String(),
    hasClientSecret: Type.Boolean(),
    enabled: Type.Boolean(),
    scopes: Type.Union([Type.String(), Type.Null()]),
    /** Provider-declared extra config (HUB-117) — keys are the manifest's field keys; `{}` when none. */
    params: Type.Record(Type.String(), Type.String()),
    /** HUB-122: sign-ins through this provider must add an authenticator app (forced enrolment when none). */
    mfaRequired: Type.Boolean(),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const OauthProviderCreateBody = Type.Object({
    provider: Type.String({ minLength: 1 }),
    clientId: Type.String({ minLength: 1 }),
    clientSecret: Type.String({ minLength: 1 }),
    enabled: Type.Optional(Type.Boolean()),
    scopes: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
export const OauthProviderUpdateBody = Type.Object({
    clientId: Type.Optional(Type.String({ minLength: 1 })),
    clientSecret: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
    scopes: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
/** Drive-connector OAuth config (response) — carries only `hasClientSecret`. */
export const ConnectorOauthSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    connector: Type.String(),
    clientId: Type.String(),
    hasClientSecret: Type.Boolean(),
    enabled: Type.Boolean(),
    scope: Type.String(),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const ConnectorOauthCreateBody = Type.Object({
    connector: Type.String({ minLength: 1 }),
    clientId: Type.String({ minLength: 1 }),
    clientSecret: Type.String({ minLength: 1 }),
    enabled: Type.Optional(Type.Boolean()),
});
export const ConnectorOauthUpdateBody = Type.Object({
    clientId: Type.Optional(Type.String({ minLength: 1 })),
    clientSecret: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
});
// -- M27 task-05 route contracts (ADM-07/08/18; HUB-083) --
/** `:connector` path param — V1 only ships the Drive connector's OAuth app. */
export const ConnectorOauthParamsSchema = Type.Object({
    connector: Type.Literal('google-drive'),
});
/** GET /identity-providers — the full login-provider config list. */
export const IdentityProviderListResponseSchema = Type.Array(OauthProviderConfigSchema);
/**
 * PUT /identity-providers/:provider — upsert one login provider. `clientId` is
 * required; `enabled` defaults off. `clientSecret` is OPTIONAL because it is
 * write-only: the admin card never gets it back, so a re-save that only flips
 * `enabled` carries none — omitting it keeps the stored ciphertext. The first
 * write for a provider must supply one (400 `client_secret_required`). The
 * plaintext secret is the ONLY secret field on the wire; it is encrypted at
 * rest and never returned.
 */
export const IdentityProviderPutBody = Type.Object({
    clientId: Type.String({ minLength: 1 }),
    clientSecret: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
    scopes: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    /**
     * The provider's declared fields (HUB-117 D3) — e.g. `{ tenantId }` for
     * Microsoft, `{ issuer }` for Okta. Validated against the manifest before
     * the secret gate; an undeclared key, a missing required field or a value
     * failing its check is a 400 carrying `param`.
     */
    params: Type.Optional(Type.Record(Type.String(), Type.String())),
    /** HUB-122: the provider's MFA-enforcement switch; off when omitted, like `enabled`. */
    mfaRequired: Type.Optional(Type.Boolean()),
});
/** 400 body of the PUT above — `param` names the offending manifest field when there is one. */
export const IdentityProviderPutErrorSchema = Type.Object({
    error: Type.String(),
    message: Type.String(),
    param: Type.Optional(Type.String()),
});
/**
 * PUT /connector-oauth/:connector — upsert the connector's OAuth app. `scope`
 * is stamped server-side to the fixed read-only Drive scope, never client-set.
 * `clientSecret` is optional on re-save for the same write-only reason as the
 * login providers above.
 */
export const ConnectorOauthPutBody = Type.Object({
    clientId: Type.String({ minLength: 1 }),
    clientSecret: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
});
/**
 * GET /admin/identity-config — read-only snapshot of the hub's identity posture
 * (JWT issuer/audience/TTLs, SSO return-to allowlist + cookie domain, active
 * email provider, first-admin bootstrap state). Contains NO secret material.
 */
export const IdentityConfigSchema = Type.Object({
    jwt: Type.Union([
        Type.Null(),
        Type.Object({
            issuer: Type.String(),
            audience: Type.String(),
            accessTokenTtlSec: Type.Number(),
            refreshTokenTtlSec: Type.Number(),
        }),
    ]),
    sso: Type.Union([
        Type.Null(),
        Type.Object({
            returnToAllowlist: Type.Array(Type.String()),
            cookieDomain: Type.Union([Type.String(), Type.Null()]),
        }),
    ]),
    emailProvider: Type.String(),
    bootstrap: Type.Object({
        enabled: Type.Boolean(),
        hasAdmin: Type.Boolean(),
    }),
    /**
     * `HUB_PUBLIC_URL` — the origin the hub puts in every OAuth `redirect_uri`.
     * The admin cards build their read-only callback URLs from it, so what an
     * operator registers at the provider is what the hub will send. Null when
     * OAuth is unconfigured (no HUB_PUBLIC_URL / APP_BASE_URL).
     */
    hubPublicUrl: Type.Union([Type.String(), Type.Null()]),
});
//# sourceMappingURL=identity-providers.js.map